import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ShieldAlert, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { fmtNum, fmtPct } from "../lib/format";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StopLossCalculator } from "../components/StopLossCalculator";

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const ScoreBar = ({ score }) => (
  <div className="h-2 bg-secondary rounded-sm overflow-hidden mt-2">
    <div className="h-full transition-none" style={{
      width: `${score}%`,
      background: score >= 70 ? "#10b981" : score >= 40 ? "#eab308" : "#ef4444",
    }} />
  </div>
);

const AIRiskRead = ({ portfolioId }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => setResult(null), [portfolioId]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/risk/${portfolioId}/ai-read`);
      setResult(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="widget p-5 bg-blue-950/20 border-primary/20" data-testid="ai-risk-read-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="font-heading font-bold text-white tracking-tight">AI risk read</span>
        </div>
        {result && (
          <span className="text-[11px] font-mono text-muted-foreground">
            {result.cached ? "CACHED" : "FRESH"} · {new Date(result.generated_at).toLocaleTimeString()}
          </span>
        )}
      </div>
      {!result ? (
        <Button onClick={run} disabled={loading} data-testid="ai-risk-read-button"
          className="rounded-sm bg-primary hover:bg-primary/85 text-white mt-4">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading risk profile…</> : "Summarise risk profile"}
        </Button>
      ) : (
        <div className="mt-4 space-y-2 fade-up" data-testid="ai-risk-read-content">
          {result.read.split("\n").map((line, i) => (
            <p key={i} className={`text-sm text-gray-100 leading-relaxed ${line.trim().startsWith("- ") ? "pl-3" : ""}`}
              dangerouslySetInnerHTML={{ __html: esc(line).replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>') || "&nbsp;" }} />
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-white/10">This is AI-generated research, not financial advice.</p>
    </div>
  );
};

export default function RiskTools() {
  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/portfolios").then((r) => {
      setPortfolios(r.data.portfolios);
      if (r.data.portfolios.length) setActiveId(r.data.portfolios[0].id);
    });
  }, []);

  useEffect(() => {
    if (window.location.hash === "#stoploss") {
      const t = setTimeout(() => document.getElementById("stoploss")?.scrollIntoView({ behavior: "smooth" }), 300);
      return () => clearTimeout(t);
    }
  }, []);

  const load = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/risk/${id}`);
      setMetrics(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeId) load(activeId);
    else setMetrics(null);
  }, [activeId, load]);

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline-label mb-2">Exposure &amp; volatility</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white">Risk Tools</h1>
        </div>
        <div className="w-64">
          <Select value={activeId} onValueChange={setActiveId}>
            <SelectTrigger className="rounded-sm bg-card h-10" data-testid="risk-portfolio-select">
              <SelectValue placeholder="Select portfolio" />
            </SelectTrigger>
            <SelectContent className="rounded-sm">
              {portfolios.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {portfolios.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="risk-no-portfolios">
          Create a portfolio first — risk metrics are computed per portfolio.
        </p>
      )}

      {loading && (
        <div className="p-10 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Computing metrics from 1y history…
        </div>
      )}

      {metrics && !metrics.empty && !loading && (
        <>
          {/* Warnings */}
          {metrics.warnings.length > 0 ? (
            <div className="space-y-2" data-testid="risk-warnings">
              {metrics.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-3 border border-yellow-500/40 bg-yellow-500/5 rounded-sm px-4 py-3"
                  data-testid={`risk-warning-${w.type}`}>
                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-gray-200">{w.message}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 border border-gain/30 bg-gain/5 rounded-sm px-4 py-3" data-testid="risk-no-warnings">
              <ShieldAlert className="w-4 h-4 text-gain" />
              <span className="text-sm text-gray-200">No concentration warnings — no holding above 20% and no sector above 40%.</span>
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="widget p-4" data-testid="portfolio-vol-card">
              <div className="overline-label">Weighted volatility (ann.)</div>
              <div className="font-mono text-2xl text-white mt-2">{fmtNum(metrics.portfolio_weighted_vol_pct, 1)}%</div>
              <p className="text-[11px] text-muted-foreground mt-1">Sum of weight × holding vol (ignores correlations)</p>
            </div>
            <div className="widget p-4" data-testid="portfolio-beta-card">
              <div className="overline-label">Beta vs S&amp;P 500</div>
              <div className="font-mono text-2xl text-white mt-2">
                {metrics.portfolio_beta_vs_sp500 != null ? fmtNum(metrics.portfolio_beta_vs_sp500) : "—"}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Computed from 1y daily returns vs ^GSPC</p>
            </div>
            <div className="widget p-4" data-testid="diversification-card">
              <div className="overline-label">Diversification score</div>
              <div className="font-mono text-2xl text-white mt-2">{metrics.diversification.score}<span className="text-sm text-muted-foreground">/100</span></div>
              <ScoreBar score={metrics.diversification.score} />
            </div>
          </div>

          {/* Suggestions */}
          <div className="widget p-4" data-testid="diversification-suggestions">
            <div className="overline-label mb-3">Improvement suggestions (rule-based)</div>
            <ul className="space-y-2">
              {metrics.diversification.suggestions.map((s, i) => (
                <li key={i} className="text-sm text-gray-200 border-l-2 border-primary/50 pl-3">{s}</li>
              ))}
            </ul>
          </div>

          {/* Holdings risk table */}
          <div className="widget overflow-hidden" data-testid="holdings-risk-table">
            <div className="px-4 py-3 border-b border-border"><span className="overline-label">Per-holding risk (1y history)</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    {["Ticker", "Sector", "Weight", "Ann. vol", "Beta", "Max drawdown 1y"].map((h, i) => (
                      <th key={h} className={`px-4 py-2.5 overline-label whitespace-nowrap ${i >= 2 ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {metrics.holdings.map((h) => (
                    <tr key={h.ticker} className="border-b border-border/50 even:bg-white/[0.02]" data-testid={`risk-row-${h.ticker}`}>
                      <td className="px-4 py-2.5 text-primary">{h.ticker}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground font-body">{h.sector}</td>
                      <td className="px-4 py-2.5 text-right text-gray-300">{fmtNum(h.weight_pct, 1)}%</td>
                      <td className="px-4 py-2.5 text-right text-gray-300">{h.annualised_vol_pct != null ? `${fmtNum(h.annualised_vol_pct, 1)}%` : "—"}</td>
                      <td className="px-4 py-2.5 text-right text-gray-300">{h.beta_vs_sp500 != null ? fmtNum(h.beta_vs_sp500) : "—"}</td>
                      <td className="px-4 py-2.5 text-right text-loss">{h.max_drawdown_1y_pct != null ? fmtPct(h.max_drawdown_1y_pct, 1) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-2.5 text-[11px] text-muted-foreground border-t border-border">{metrics.note}</p>
          </div>

          <AIRiskRead portfolioId={activeId} />
        </>
      )}

      {metrics?.empty && !loading && (
        <p className="text-sm text-muted-foreground" data-testid="risk-empty-portfolio">{metrics.message}</p>
      )}

      <div id="stoploss">
        <StopLossCalculator />
      </div>
    </div>
  );
}
